import { ApiClientProvider } from '@sdx/fe-utils';
import type { AuthTokens } from '@sdx/fe-utils';
import { useQueryClient } from '@tanstack/react-query';
import type { JSX, ReactNode } from 'react';
import { useMemo, useRef } from 'react';
import { useAuth } from '../../core/contexts/AuthContext';
import { addDatadogError, normalizeErrorForTelemetry } from '../../core/services/telemetry/datadog';

interface ApiProviderProps {
  children: ReactNode;
}

const ApiProvider = ({ children }: ApiProviderProps): JSX.Element => {
  const queryClient = useQueryClient();
  const { accessToken, accessTokenRef, checkSession, waitForNextToken } = useAuth();
  // Tracks the token this getTokens() closure last handed back, so the already-rotated
  // short-circuit below can't re-serve the same token on the package's second retry: if that
  // token also 401s, accessTokenRef.current still equals it (nothing rotated it again), so
  // without this the short-circuit would fire again with the identical known-bad token instead
  // of falling through to an actual checkSession()/waitForNextToken() refresh.
  const lastServedTokenRef = useRef<string | undefined>(undefined);

  // Without this, @sdx/fe-utils's useAxios() has no authContext at all: on a 401 it silently
  // rejects with no refresh, no retry, and no signal back to AuthContext — every shell-level call
  // (profile, notifications, kpi, menu, favorites, news, ...) just fails outright once the held
  // token goes stale. getTokens() must THROW on failure rather than resolve with an empty token —
  // resolving lets the package's own retry loop (bounded at 2 attempts) blindly retry a second
  // time with no Authorization header before giving up, doubling the wait for nothing.
  const authContext = useMemo(
    () => ({
      // MUST stay empty — never populate accessToken here. @sdx/fe-utils@2.4.1's useAxios()
      // retries a 401 by calling `axiosInstance(originalRequest)`, which re-runs this SAME
      // instance's request interceptor. That interceptor stamps the Authorization header from
      // THIS closed-over `tokens` object, not from whatever getTokens() just returned — so a
      // populated tokens.accessToken overwrites the fresh header the retry is trying to send
      // with this render's stale snapshot, silently undoing getTokens() (and its already-rotated
      // short-circuit) on every single retry. Leaving accessToken undefined makes that
      // interceptor's addAuthHeaders() a no-op, so the header getTokens() set survives. The
      // initial (pre-401) Authorization header still comes from axiosConfig.headers below.
      tokens: {} satisfies AuthTokens,
      getTokens: async (): Promise<AuthTokens> => {
        // `accessToken` here is the value this specific getTokens closure was built with — the
        // token the axios instance that issued the failing request actually used. @sdx/fe-utils
        // creates a fresh axios instance (and interceptor closures) on every ApiProvider render,
        // but an ALREADY IN-FLIGHT request stays bound to whichever closure existed when it was
        // sent, even if ApiProvider has since re-rendered with a newer token. So if ContainerApp's
        // own proactive refresh (or any other checkSession() call) already rotated the token
        // before this 401 was even processed, accessTokenRef.current is already fresher than what
        // this closure captured — waitForNextToken() would never see that as a FUTURE change (it
        // already happened), and would time out for no reason. Detect that directly instead of
        // waiting for an event that already fired.
        if (
          accessTokenRef.current &&
          accessTokenRef.current !== accessToken &&
          accessTokenRef.current !== lastServedTokenRef.current
        ) {
          lastServedTokenRef.current = accessTokenRef.current;
          return { accessToken: accessTokenRef.current };
        }

        checkSession();
        const gotFreshToken = await waitForNextToken();
        if (!gotFreshToken || !accessTokenRef.current) {
          // lastServedTokenRef is one shared slot, but getTokens() gets no per-request identity
          // from @sdx/fe-utils — a concurrent request that never touched that token (e.g. it was
          // rotated by a proactive refresh, not by this request's own 401) can land here purely
          // because another in-flight request's retry already claimed the short-circuit above. A
          // token this closure never actually used is still better than failing outright, so
          // serve it before giving up — this doesn't undo the lastServedTokenRef fix above: that
          // guard already forced a real checkSession()/waitForNextToken() attempt first, this is
          // only the last-resort fallback once that attempt found nothing newer.
          if (accessTokenRef.current && accessTokenRef.current !== accessToken) {
            return { accessToken: accessTokenRef.current };
          }
          // Shaped to match isUnauthorizedError's structural check so a caller using
          // retryUnless401 fails closed on this instead of retrying it as a generic error.
          throw Object.assign(new Error('Unable to refresh access token'), { status: 401 });
        }
        lastServedTokenRef.current = accessTokenRef.current;
        return { accessToken: accessTokenRef.current };
      },
      onInvalidAccessToken: () => {
        const { error: safeError, context } = normalizeErrorForTelemetry(
          new Error('Invalid or expired token'),
          { genericMessage: 'Invalid or expired token' },
        );
        addDatadogError(safeError, { source: 'ApiProvider.onInvalidAccessToken', ...context });
      },
    }),
    [accessToken, accessTokenRef, checkSession, waitForNextToken],
  );

  return (
    <ApiClientProvider
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      queryClient={queryClient as any}
      authContext={authContext}
      axiosConfig={{
        headers: {
          'app-code': 'GLOBAL_CONTAINER_APP',
          ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
        },
      }}
    >
      {children}
    </ApiClientProvider>
  );
};
export default ApiProvider;
